import React from "react";
import defaultLogo from "../../assets/images/cicoplogo.png";
import { useApp } from "../../context/AppContext";

/**
 * CollegePrintHeader
 * ──────────────────
 * Standardized, executive-grade print and screen header for all official documents:
 * Fee Receipts, Student Statements, Ledger Reports, Salary Payslips, Attendance Reports, etc.
 *
 * @param {Object} college - College object containing { name, sansthaName, logo, address, city, state, pincode, contactPhone, contactEmail }
 * @param {string} title - Main document title (e.g. "FEE RECEIPT", "STUDENT FEE STATEMENT")
 * @param {string} subtitle - Secondary title or academic year
 * @param {React.ReactNode} rightContent - Optional right-aligned meta block (e.g. Receipt No, Date)
 * @param {"banner"|"centered"|"compact"} layout - Header layout style
 * @param {number} logoSize - Dimensions for the logo in px (default: 68)
 * @param {boolean} showBorders - Whether to display clean separator bottom border (default: true)
 */
export default function CollegePrintHeader({
    college: propCollege,
    title,
    subtitle,
    rightContent,
    layout = "banner",
    logoSize = 68,
    showBorders = true,
    style = {},
}) {
    const { user, collegeData } = useApp?.() || {};
    const college = propCollege || collegeData || user?.linkedCollege || {};

    const rawLogo = college?.logo?.url || (typeof college?.logo === "string" ? college.logo : "") || defaultLogo;
    const sansthaName = college?.sansthaName || "MEHMUDA SHIKSHAN & MAHILA GRAMIN VIKAS BAHUDDESHIYA SANSTHA, NAGPUR";
    const collegeName = college?.name || "CENTRAL INDIA COLLEGE OF PHARMACY";

    // Build location/address line
    const addressParts = [
        college?.address || "Kh. No. 25/1 B, Mouza-Lonara",
        college?.city || "Nagpur",
        college?.pincode ? `Pin: ${college.pincode}` : "441 111",
    ].filter(Boolean);

    const contactParts = [];
    if (college?.contactPhone) contactParts.push(`Ph: ${college.contactPhone}`);
    if (college?.contactEmail) contactParts.push(`Email: ${college.contactEmail}`);

    if (layout === "centered") {
        return (
            <div
                className="college-print-header centered"
                style={{
                    textAlign: "center",
                    borderBottom: showBorders ? "2px solid #0f172a" : "none",
                    paddingBottom: "12px",
                    marginBottom: "14px",
                    position: "relative",
                    ...style,
                }}
            >
                {rawLogo && (
                    <div style={{ marginBottom: 6, display: "flex", justifyContent: "center" }}>
                        <img
                            src={rawLogo}
                            alt={collegeName}
                            style={{
                                width: logoSize,
                                height: logoSize,
                                objectFit: "contain",
                                filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.08))",
                            }}
                        />
                    </div>
                )}
                <div style={{
                    fontSize: "9.5px",
                    fontWeight: 700,
                    letterSpacing: "0.8px",
                    color: "#475569",
                    textTransform: "uppercase",
                    lineHeight: 1.2
                }}>
                    {sansthaName}
                </div>
                <div style={{
                    fontSize: "18px",
                    fontWeight: 900,
                    color: "#0f172a",
                    letterSpacing: "0.5px",
                    lineHeight: 1.2,
                    margin: "3px 0 2px"
                }}>
                    {collegeName}
                </div>
                <div style={{ fontSize: "10.5px", color: "#334155" }}>
                    {addressParts.join(", ")}
                    {contactParts.length > 0 && ` · ${contactParts.join(" · ")}`}
                </div>

                {title && (
                    <div style={{
                        marginTop: 10,
                        display: "flex",
                        justifyContent: rightContent ? "space-between" : "center",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: "6px 12px"
                    }}>
                        <div style={{ textAlign: rightContent ? "left" : "center" }}>
                            <div style={{
                                fontSize: "14px",
                                fontWeight: 800,
                                textDecoration: "underline",
                                textUnderlineOffset: "3px",
                                color: "#0f172a",
                                letterSpacing: "0.4px"
                            }}>
                                {title}
                            </div>
                            {subtitle && (
                                <div style={{ fontSize: "10.5px", color: "#64748b", marginTop: 2 }}>
                                    {subtitle}
                                </div>
                            )}
                        </div>
                        {rightContent && <div>{rightContent}</div>}
                    </div>
                )}
            </div>
        );
    }

    if (layout === "compact") {
        return (
            <div
                className="college-print-header compact"
                style={{
                    borderBottom: showBorders ? "2px solid #0f172a" : "none",
                    paddingBottom: "10px",
                    marginBottom: "12px",
                    ...style,
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    {rawLogo && (
                        <div style={{ flexShrink: 0 }}>
                            <img
                                src={rawLogo}
                                alt={collegeName}
                                style={{
                                    width: logoSize || 50,
                                    height: logoSize || 50,
                                    objectFit: "contain",
                                }}
                            />
                        </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                            fontSize: "8.5px",
                            fontWeight: 700,
                            letterSpacing: "0.5px",
                            color: "#64748b",
                            textTransform: "uppercase"
                        }}>
                            {sansthaName}
                        </div>
                        <div style={{
                            fontSize: "15px",
                            fontWeight: 800,
                            color: "#0f172a",
                            lineHeight: 1.2,
                            letterSpacing: "0.3px",
                            margin: "1px 0"
                        }}>
                            {collegeName}
                        </div>
                        <div style={{ fontSize: "9.5px", color: "#475569" }}>
                            {addressParts.join(", ")} {contactParts.length > 0 && `· ${contactParts[0]}`}
                        </div>
                    </div>
                    {rightContent && <div style={{ flexShrink: 0 }}>{rightContent}</div>}
                </div>

                {title && (
                    <div style={{
                        marginTop: 8,
                        paddingTop: 6,
                        borderTop: "1px dashed #cbd5e1",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                    }}>
                        <div style={{ fontSize: "13px", fontWeight: 800, color: "#0f172a" }}>
                            {title}
                        </div>
                        {subtitle && <div style={{ fontSize: "10px", color: "#64748b" }}>{subtitle}</div>}
                    </div>
                )}
            </div>
        );
    }

    // Default: "banner" layout (Logo on Left, Center Title Info, Right Meta Box)
    return (
        <div
            className="college-print-header banner"
            style={{
                borderBottom: showBorders ? "2.5px solid #0f172a" : "none",
                paddingBottom: "12px",
                marginBottom: "14px",
                ...style,
            }}
        >
            <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "14px",
                flexWrap: "nowrap"
            }}>
                {/* Left: Logo */}
                {rawLogo && (
                    <div style={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
                        <img
                            src={rawLogo}
                            alt={collegeName}
                            style={{
                                width: logoSize,
                                height: logoSize,
                                objectFit: "contain",
                                filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.1))",
                            }}
                        />
                    </div>
                )}

                {/* Center: Institution Branding */}
                <div style={{
                    flex: 1,
                    textAlign: "center",
                    minWidth: 0,
                    padding: "0 4px"
                }}>
                    <div style={{
                        fontSize: "9px",
                        fontWeight: 700,
                        letterSpacing: "0.8px",
                        color: "#475569",
                        textTransform: "uppercase",
                        lineHeight: 1.2
                    }}>
                        {sansthaName}
                    </div>
                    <div style={{
                        fontSize: "17.5px",
                        fontWeight: 900,
                        color: "#0f172a",
                        letterSpacing: "0.4px",
                        lineHeight: 1.15,
                        margin: "3px 0 2px"
                    }}>
                        {collegeName}
                    </div>
                    <div style={{ fontSize: "10px", color: "#334155", lineHeight: 1.3 }}>
                        {addressParts.join(", ")}
                    </div>
                    {contactParts.length > 0 && (
                        <div style={{ fontSize: "9.5px", color: "#64748b", marginTop: 1 }}>
                            {contactParts.join("  |  ")}
                        </div>
                    )}
                </div>

                {/* Optional Right: QR, seal placeholder or extra right content */}
                {rightContent && (
                    <div style={{ flexShrink: 0, textAlign: "right" }}>
                        {rightContent}
                    </div>
                )}
            </div>

            {/* Document Title Bar */}
            {title && (
                <div style={{
                    marginTop: 10,
                    paddingTop: 8,
                    borderTop: "1px solid #e2e8f0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "4px"
                }}>
                    <div>
                        <span style={{
                            display: "inline-block",
                            fontSize: "13.5px",
                            fontWeight: 900,
                            letterSpacing: "0.5px",
                            color: "#0f172a",
                            textTransform: "uppercase"
                        }}>
                            {title}
                        </span>
                        {subtitle && (
                            <span style={{ fontSize: "11px", color: "#64748b", marginLeft: 8 }}>
                                {subtitle}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
